/**
 * RequirePerm — the route guard that closes the "type the URL" hole.
 *
 * Before this, `App.tsx` guarded all 24 protected routes with `isAuthenticated`
 * only: the sidebar hid items by role but nothing stopped a direct URL visit.
 * Verified live example — `Layout.tsx` restricts Domain Identities to
 * `agents_manager`, yet a `viewer` could reach `/domain-identities` and create
 * or delete email / Help Center domains (Plan-RBAC-SchemaRoles-2026-08.md §3a).
 *
 * ── How it decides ──────────────────────────────────────────────────────────
 * The permission comes from `config/navigation.ts`, the SAME metadata the
 * sidebar renders from. There is no second role list to keep in sync, so the
 * URL can never be more permissive than the menu.
 *
 * ── Routes with no nav entry are NOT guarded ────────────────────────────────
 * `findNavItem()` returns undefined and the guard passes through. This is
 * deliberate, not a gap:
 *   `/reports`   — unlisted on purpose but live; the escape hatch for emailing
 *                  a report to someone who is not workspace staff.
 *   `/billing`   — guarded server-side by its own inline `require_admin`.
 *   `/profile`   — self-scoped, and one of the two guaranteed landing spots.
 *   `/qr-codes`  — public, no auth, no backend router.
 * Guarding any of those would break a real workflow. Adding a nav entry for
 * them would silently guard them — see the warning in navigation.ts.
 *
 * ── Fail-safe posture ───────────────────────────────────────────────────────
 * 🚨 Renders `<NoAccessPanel />` in place rather than redirecting: a redirect
 * loops forever for a role that matches nothing (§9.1).
 * 🚨 Waits for the effective role set to settle before denying — an in-flight
 * or errored members fetch must never read as "no permissions".
 * 🚨 Flag-gated on `rbac_route_guards`, feature-detected fail-closed inside
 * `usePermissions`. Absent or false ⇒ today's behavior exactly.
 */
import React, { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { findNavItem } from '../config/navigation';
import usePermissions from '../hooks/usePermissions';
import NoAccessPanel from './NoAccessPanel';

interface RequirePermProps {
  /**
   * Nav href this route's permission is drawn from. Defaults to the current
   * pathname; pass it explicitly for a sub-route that inherits its parent's
   * permission (e.g. `/integrations/docs` → `/integrations`).
   */
  navHref?: string;
  children: ReactNode;
}

const RequirePerm: React.FC<RequirePermProps> = ({ navHref, children }) => {
  const location = useLocation();
  const { guardsEnabled, permsLoading, canAccessNavItem } = usePermissions();

  // Dark by default — no behavior change until the flag is flipped.
  if (!guardsEnabled) return <>{children}</>;

  const item = findNavItem(navHref ?? location.pathname);
  // No nav entry ⇒ no guard, by design (see the header).
  if (!item) return <>{children}</>;

  if (permsLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!canAccessNavItem(item)) return <NoAccessPanel pageName={item.name} />;

  return <>{children}</>;
};

export default RequirePerm;
